import React, { useMemo } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { CustomProvider, ReactRouteObject } from '../utils/routeUtil';
import { connectUtil, PropsFromRedux } from '../utils/reduxUtil';
import { RootStateBase } from '../store/baseStore';
import App from '../App';
import SideBarOptions from '../utils/SideBarOptions';

interface DynamicRouterProviderProps {
  routes: ReactRouteObject[] | undefined;
  fallbackElement?: React.ReactNode;
  sistemaID: number;
  sidebarOptions?: SideBarOptions[];
  headerLeftInfo?: React.ReactNode | JSX.Element;
  customWrapper?: React.ComponentType<CustomProvider>
  defaultRouter: ReturnType<typeof createBrowserRouter>;
}

const connector = connectUtil(
  (state: RootStateBase) => ({
    conditionalRoute: state.UtilReducer.ConditionalRoute,
  }),
  { }
);

export interface DynamicRouterProvider extends PropsFromRedux<typeof connector> {}

// Função para detectar se estamos em ambiente de desenvolvimento com base URL
function getBasename(): string {
  // Verifica se estamos em build:dev usando variáveis de ambiente
  const isDev = import.meta.env.MODE === 'development';
  const baseUrl = import.meta.env.BASE_URL;
  
  // Se está em desenvolvimento e tem uma base URL diferente de '/', usa ela
  if (isDev && baseUrl && baseUrl !== '/') {
    return baseUrl.replace(/\/$/, ''); // Remove trailing slash
  }
  
  return '/';
}

function DynamicRouterProvider(props: DynamicRouterProviderProps & PropsFromRedux<typeof connector>) {
    const { routes, conditionalRoute, fallbackElement, sistemaID, sidebarOptions, headerLeftInfo, customWrapper, defaultRouter } = props;

    const dynamicRouter = useMemo(() => {
        if (!routes) {
            return defaultRouter;
        }

        const enabledRoutes = routes.filter(route => {
            const routeState = conditionalRoute.find(cr => cr.Path === route.path);
            return routeState ? routeState.Status !== false : true;
        });

        const basename = getBasename();

        return createBrowserRouter([
            {
                element: (
                    <App
                        routes={enabledRoutes}
                        sistemaID={sistemaID}
                        SidebarOptions={sidebarOptions}
                        headerLeftInfo={headerLeftInfo}
                        customWrapper={customWrapper}
                    />
                ),
                children: enabledRoutes,
            },
            {
                path: "*",
                element: fallbackElement,
                errorElement: routes.find(r => r.path === "*")?.errorElement,
            },
        ], { basename });
    }, [routes, conditionalRoute, fallbackElement, sistemaID, sidebarOptions, headerLeftInfo, customWrapper, defaultRouter]);

    // Cria uma chave única baseada no estado das rotas condicionais para forçar re-renderização do RouterProvider
    const routerKey = useMemo(() => {
        return conditionalRoute
            .map(route => `${route.Path}:${route.Status}`)
            .sort()
            .join('|');
    }, [conditionalRoute]);
    
    return <RouterProvider key={routerKey} router={dynamicRouter} />;
}

export default connector(DynamicRouterProvider);
